import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Checkbox,
} from '@mui/material';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { checkComponentPermission, isAdmin } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import TableHeadCustom from '../../../components/table/table-head-custom';
import TableSelectedAction from '../../../components/table/table-selected-action';
import LeaveStatusBadge from './LeaveStatusBadge';
import { LEAVE_TYPE_LABELS, LEAVE_STATUS } from '../constants';

const SYSTEM_AUTO_LEAVE_REASON = 'SYSTEM_AUTO_APPROVED';

const LeaveTable = ({
  leaves,
  onCancelLeave,
  onEditLeave,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  sort = {},
  onSort,
  selectedIds = [],
  selectableIds = [],
  onSelectionChange,
  selectionActions,
}) => {
  const navigate = useNavigate();
  const selectable = Boolean(onSelectionChange);

  // `id` must match the backend `sortBy` values; columns with `render` are not sortable.
  const headLabel = [
    ...(showEmployee ? [{ id: 'employee', label: 'Employee' }] : []),
    { id: 'leaveType', label: 'Leave Type' },
    { id: 'startDate', label: 'Start Date' },
    { id: 'endDate', label: 'End Date' },
    { id: 'totalDays', label: 'Days' },
    { id: 'status', label: 'Status' },
    ...(showEmployee
      ? [
          { id: 'reviewedBy', label: 'Reviewed By', render: () => 'Reviewed By' },
          { id: 'reviewedAt', label: 'Reviewed Date' },
        ]
      : []),
    { id: 'actions', label: 'Actions', render: () => 'Actions' },
  ];

  const selectableOnPage = (leaves || []).filter((leave) => selectableIds.includes(leave.id)).map((leave) => leave.id);

  const handleSelectAll = (checked) => onSelectionChange(checked ? selectableOnPage : []);

  const handleSelectRow = (leaveId) =>
    onSelectionChange(
      selectedIds.includes(leaveId) ? selectedIds.filter((id) => id !== leaveId) : [...selectedIds, leaveId]
    );

  const isUnplannedLeave = (leaveItem) => {
    if (!leaveItem?.startDate || !leaveItem?.createdAt) return false;
    // "Unplanned" means the request was created after the leave's start date.
    const start = dayjs(leaveItem.startDate).startOf('day');
    const created = dayjs(leaveItem.createdAt).startOf('day');
    return created.isAfter(start);
  };

  const isSystemApprovedLeave = (leaveItem) => leaveItem?.reason === SYSTEM_AUTO_LEAVE_REASON;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ position: 'relative' }}>
        {selectable && (
          <TableSelectedAction
            numSelected={selectedIds.length}
            rowCount={selectableOnPage.length}
            onSelectAllRows={handleSelectAll}
            action={selectionActions}
          />
        )}
        <Table>
          <TableHeadCustom
            headLabel={headLabel}
            order={sort.sortOrder}
            orderBy={sort.sortBy}
            onSort={onSort}
            rowCount={selectableOnPage.length}
            numSelected={selectedIds.length}
            onSelectAllRows={selectable ? handleSelectAll : undefined}
          />
          <TableBody>
            {leaves && leaves.length > 0 ? (
              leaves.map((leave) => (
                <TableRow key={leave.id} hover selected={selectedIds.includes(leave.id)}>
                  {selectable && (
                    <TableCell padding="checkbox">
                      {selectableIds.includes(leave.id) && (
                        <Checkbox checked={selectedIds.includes(leave.id)} onChange={() => handleSelectRow(leave.id)} />
                      )}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{leave.employee?.userName || leave.employee?.email || '-'}</TableCell>}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <span>
                        {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                        {leave.isHalfDay && ` (Half Day)`}
                      </span>
                      {isUnplannedLeave(leave) && (
                        <Chip label="Unplanned" color="warning" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(leave.startDate)}</TableCell>
                  <TableCell>{formatDate(leave.endDate)}</TableCell>
                  <TableCell>{leave.totalDays}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <LeaveStatusBadge status={leave.status} />
                      {isSystemApprovedLeave(leave) && (
                        <Chip label="System Approved" color="info" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {leave.approver && leave.approver.id === currentUserId
                        ? 'Me'
                        : leave.approver?.userName || leave.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(leave.reviewedAt || leave.approvedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leaves/${leave.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {checkComponentPermission('UPDATE_LEAVE') &&
                        ((!showEmployee && leave.status === LEAVE_STATUS.PENDING && leave.userId === currentUserId) ||
                          (isAdmin() && checkComponentPermission('APPROVE_LEAVE'))) && (
                          <Tooltip title="Edit Leave">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditLeave(leave.id);
                              }}
                            >
                              <Iconify icon="eva:edit-fill" />
                            </IconButton>
                          </Tooltip>
                        )}
                      {!showEmployee && leave.status === LEAVE_STATUS.PENDING && leave.userId === currentUserId && (
                        <>
                          {checkComponentPermission('DELETE_LEAVE') && (
                            <Tooltip title="Delete Leave">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelLeave(leave.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headLabel.length + (selectable ? 1 : 0)} align="center">
                  No leave requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

LeaveTable.propTypes = {
  leaves: PropTypes.array.isRequired,
  onCancelLeave: PropTypes.func,
  onEditLeave: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  sort: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.oneOf(['asc', 'desc']) }),
  onSort: PropTypes.func,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  selectableIds: PropTypes.arrayOf(PropTypes.number),
  onSelectionChange: PropTypes.func,
  selectionActions: PropTypes.node,
};

export default LeaveTable;
